import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LANGS } from "../../constants/modules";
import { useThemeContext } from "./ThemeProvider";
import { ROUTES } from "../../routes";
import { useAuth } from "../../contexts/AuthContext";

interface AppShellProps {
  lang: string;
  setLang: (l: string) => void;
  children: React.ReactNode;
}

const SHELLLESS_ROUTES = new Set<string>([ROUTES.LOGIN, ROUTES.SIGNUP, ROUTES.CARTE_PUBLIC]);

export function AppShell({ lang, setLang, children }: AppShellProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { theme, setTheme } = useThemeContext();

  if (!user || SHELLLESS_ROUTES.has(pathname)) {
    return <div className="min-h-screen bg-background text-foreground">{children}</div>;
  }

  const navItems = [
    { id: "vinai", label: "VinAI", route: ROUTES.VINAI, dot: "bg-accent", active: pathname === ROUTES.VINAI },
    { id: "cave", label: "Ma cave", route: ROUTES.CARTE, dot: "bg-[#CC9A3E]", active: pathname === ROUTES.CARTE },
    { id: "dashboard", label: "Dashboard", route: ROUTES.DASHBOARD, dot: "bg-[#2D6A9F]", active: pathname === ROUTES.DASHBOARD },
    { id: "customers", label: "Clients", route: ROUTES.CUSTOMERS, dot: "bg-[#2E8B57]", active: pathname === ROUTES.CUSTOMERS },
    { id: "orders", label: "Commandes", route: ROUTES.ORDERS, dot: "bg-[#CC9A3E]", active: pathname === ROUTES.ORDERS },
    { id: "public", label: "Carte publique", route: ROUTES.CARTE_PUBLIC, dot: "bg-[#A8324A]", active: pathname === ROUTES.CARTE_PUBLIC },
    { id: "profile", label: "Profil", route: ROUTES.PROFILE, dot: "bg-[#8E4A6B]", active: pathname === ROUTES.PROFILE },
  ];

  const pageTitle = pathname === ROUTES.CARTE
    ? "Ma cave"
    : pathname === ROUTES.DASHBOARD
      ? "Dashboard"
    : pathname === ROUTES.CUSTOMERS
      ? "Clients"
    : pathname === ROUTES.ORDERS
      ? "Commandes"
    : pathname === ROUTES.PROFILE
      ? "Profil"
      : "Studio de génération";

  const pageDescription = pathname === ROUTES.CARTE
    ? "Gérez vos vins, publiez votre carte et gardez le QR prêt à partager."
    : pathname === ROUTES.DASHBOARD
      ? "Suivez les emails partagés, les ouvertures, les ajouts au panier et les intentions de commande."
    : pathname === ROUTES.CUSTOMERS
      ? "Centralisez vos clients, retrouvez-les vite et importez-les en un seul flux."
    : pathname === ROUTES.ORDERS
      ? "Suivez les commandes reçues, traitez-les et gardez une vue nette sur l'activité."
    : pathname === ROUTES.PROFILE
      ? "Préférences du compte et accès à l'espace VinIA."
      : "Choisissez un module, donnez le vin, laissez VinIA écrire la suite.";

  const initials = (user.displayName || user.email || "V")
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  return (
    <div className="min-h-screen bg-background text-foreground shell-grid lg:h-screen lg:overflow-hidden">
      <aside className="hidden lg:flex lg:h-screen lg:w-[248px] lg:flex-col lg:justify-between lg:overflow-hidden lg:border-r lg:border-border lg:bg-surface/90 lg:px-4 lg:py-6 lg:backdrop-blur">
        <div>
          <button
            onClick={() => navigate(ROUTES.VINAI)}
            className="flex items-center gap-3 rounded-[18px] border-none bg-transparent px-2 py-1 text-left"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-accent text-[22px] font-display font-extrabold text-[#FFF6EE] shadow-[0_10px_24px_rgba(217,102,60,0.28)]">v</span>
            <span>
              <span className="block font-display text-[22px] font-bold tracking-[-0.04em] text-foreground">vinIA</span>
              <span className="mt-0.5 block text-[10px] uppercase tracking-[0.25em] text-tertiary">Studio vin</span>
            </span>
          </button>

          <nav className="mt-8 flex flex-col gap-1.5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.route)}
                className={`flex min-h-touch items-center gap-3 rounded-[16px] border px-4 py-3 text-left text-[15px] font-semibold transition-all ${item.active ? "border-accent-border bg-accent/10 text-accent shadow-[0_8px_22px_rgba(217,102,60,0.10)]" : "border-transparent bg-transparent text-tertiary hover:bg-foreground/5 hover:text-foreground"}`}
              >
                <span className={`h-2 w-2 rounded-full ${item.active ? item.dot : "bg-foreground/20"}`} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="space-y-4">
          <div className="rounded-[18px] border border-border bg-background/65 p-1">
            <div className="grid grid-cols-2 gap-1">
              {[
                { id: "light", label: "Clair" },
                { id: "dark", label: "Sombre" },
              ].map((option) => {
                const active = theme === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() => setTheme(option.id as "light" | "dark")}
                    className={`flex min-h-[42px] items-center justify-center gap-2 rounded-[14px] border-none text-[12px] font-bold transition-all ${active ? "bg-surface text-accent shadow-[0_3px_10px_rgba(58,20,32,0.10)]" : "bg-transparent text-tertiary"}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full border-2 ${option.id === "light" ? "border-[#CC9A3E] bg-[#F1E5D4]" : "border-[#2A1019] bg-[#2A1019]"}`} />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-[18px] border border-border bg-surface-2/65 px-3 py-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,#CC9A3E,#D9663C)] font-display text-sm font-extrabold text-white">
              {initials || "V"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-foreground">{user.displayName || user.email || "Compte VinIA"}</div>
              <div className="text-[11px] text-tertiary">Plan Studio</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:min-h-0 lg:overflow-hidden">
        <header className="sticky top-0 z-20 border-b border-border bg-background/80 px-4 py-4 backdrop-blur lg:px-8">
          <div className="mx-auto flex w-full max-w-[1120px] items-center justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/10 px-3 py-1 text-[12px] font-semibold text-accent">
                <span className="h-2 w-2 rounded-full bg-accent pulse-dot" />
                Interface VinIA
              </div>
              <h1 className="mt-3 font-display text-[28px] font-bold tracking-[-0.05em] text-foreground lg:text-[38px]">{pageTitle}</h1>
              <p className="mt-1 max-w-[620px] text-[14px] text-secondary lg:text-[16px]">{pageDescription}</p>
            </div>

            <div className="flex items-center gap-2">
              {pathname === ROUTES.VINAI && (
                <div className="hidden rounded-full border border-border bg-surface/80 p-1 sm:flex">
                  {LANGS.map((item) => {
                    const active = lang === item.v;
                    return (
                      <button
                        key={item.v}
                        onClick={() => setLang(item.v)}
                        className={`min-h-[38px] rounded-full px-3 text-[12px] font-bold transition-all ${active ? "bg-accent text-[#FFF6EE] shadow-[0_8px_20px_rgba(217,102,60,0.24)]" : "text-tertiary"}`}
                      >
                        {item.l}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={() => navigate(ROUTES.PROFILE)}
                className="flex h-11 w-11 items-center justify-center rounded-[14px] border border-border bg-surface/80 text-tertiary transition-colors hover:text-foreground"
                aria-label="Ouvrir le profil"
              >
                <span className="font-display text-sm font-extrabold">{initials || "V"}</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col">
          {children}
        </main>
      </div>
    </div>
  );
}
